import { NetworkInfo, OldNetworkInfo } from '@blisspointmedia/bpm-types/dist/StreamingNetworks';
import { Platform, StreamingNetworkRecord } from './streaming-networks-constants';

// Scalar/list fields are identical between the two endpoints (same base `streaming_networks` table
// and shared joins) — the new response is the base since it's the forward-looking model, `oldRecord`
// only contributes `attributes` (the new endpoint never fetches that table at all).
export const mergeNetworkRecords = (
  oldRecord: OldNetworkInfo | undefined,
  newRecord: NetworkInfo | undefined
): StreamingNetworkRecord | undefined => {
  const base = newRecord ?? oldRecord;
  if (!base) return undefined;
  return {
    shortCode: base.shortCode,
    networkGroup: base.networkGroup,
    name: base.name,
    color: base.color,
    pdfText: base.pdfText,
    parent: base.parent,
    buyable: base.buyable,
    cpm: base.cpm,
    defaultIoPdfText: base.defaultIoPdfText,
    ioMinimum: base.ioMinimum,
    lineItemMinimum: base.lineItemMinimum,
    splitLength: base.splitLength,
    platforms: base.platforms,
    dsp: base.dsp,
    contacts: base.contacts,
    properties: base.properties,
    globalContent: newRecord?.globalContent ?? [],
    contentMetaDataMap: newRecord?.contentMetaDataMap ?? {},
    attributes: oldRecord?.attributes
  };
};

// ---- search across a network and its children ----

export interface NetworkSearchBlobs {
  name: string;
  contacts: string;
  content: string;
}

export const buildSearchBlobs = (network: StreamingNetworkRecord): NetworkSearchBlobs => ({
  name: [network.name, network.shortCode, network.networkGroup].join(' ').toLowerCase(),
  contacts: network.contacts
    .filter(contact => !contact.deleted)
    .map(contact => `${contact.first ?? ''} ${contact.last ?? ''} ${contact.email ?? ''}`)
    .join(' ')
    .toLowerCase(),
  content: network.globalContent
    .filter(content => !content.deleted)
    .map(content => content.contentValue ?? '')
    .join(' ')
    .toLowerCase()
});

export const matchSearchBlobs = (blobs: NetworkSearchBlobs, rawQuery: string): boolean => {
  const query = rawQuery.trim().toLowerCase();
  if (!query) return true;
  return (
    blobs.name.includes(query) || blobs.contacts.includes(query) || blobs.content.includes(query)
  );
};

// ---- Attributes tab row keys ----

export interface RowKeyParts {
  platform: string;
  description: string;
  property?: string;
}

// Row keys are "platform_description" (or "platform_description_property" for networks with
// sub-properties) — the naive split on '_' is a known limitation: an underscore inside a
// platform/description value would misparse here.
export const parseRowKey = (rowKey: string): RowKeyParts => {
  const [platform = '', description = '', property] = rowKey.split('_');
  return { platform, description, property };
};

export const buildRowKey = (platform: string, description: string, property?: string): string =>
  property === undefined ? `${platform}_${description}` : `${platform}_${description}_${property}`;

// ---- Content Details tab Publisher Genre multi-select ----

// Publisher Genre is stored as a sorted, semicolon-joined string of multiple values — it's the one
// multi-select field in this set.
export const parsePublisherGenre = (value: string | null): string[] =>
  value ? value.split(';').filter(Boolean) : [];

export const joinPublisherGenre = (values: string[]): string | null =>
  values.length > 0 ? [...values].sort().join(';') : null;

// ---- platform select ----

const isBonusPlatform = (name: string): boolean => name.toLowerCase().includes('bonus');

// Saved names can be null, blank or padded; compare on the trimmed value.
export const trimmedPlatformName = (row: Platform): string => (row.platform ?? '').trim();

// Distinct, trimmed, non-blank platform names across every network, for the Platform select.
// Excludes "Bonus" platforms.
export const collectPlatformOptions = (networks: StreamingNetworkRecord[]): string[] => {
  const names = networks.flatMap(network =>
    network.platforms.filter(row => !row.deleted).map(trimmedPlatformName)
  );
  return [...new Set(names)].filter(name => name && !isBonusPlatform(name)).sort();
};

// Platform names currently selected (visible, non-blank, non-Bonus) -- the multi-select's value.
export const selectedPlatformNames = (platforms: Platform[]): string[] => [
  ...new Set(
    platforms
      .filter(row => !row.deleted)
      .map(trimmedPlatformName)
      .filter(name => name && !isBonusPlatform(name))
  )
];

// Applies the multi-select's new value to the platform rows: unselected saved rows (including
// blank ones) are soft-deleted (indices stay stable), unselected new rows are dropped,
// re-selecting restores a soft-deleted row (keeping its buyable flag), and newly selected names
// become new rows. Bonus rows are untouched.
export const applyPlatformSelection = (platforms: Platform[], names: string[]): Platform[] => {
  const kept = platforms.flatMap((row): Platform[] => {
    const name = trimmedPlatformName(row);
    if (isBonusPlatform(name)) return [row];
    if (names.includes(name)) return [{ ...row, deleted: false }];
    return row.isNew ? [] : [{ ...row, deleted: true }];
  });
  const present = new Set(kept.filter(row => !row.deleted).map(trimmedPlatformName));
  const added = names
    .filter(name => !present.has(name))
    .map((name): Platform => ({ platform: name, buyable: true, isNew: true }));
  return [...kept, ...added];
};
